import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Lock, Mail, AlertCircle, Eye, EyeOff } from 'lucide-react';
import logoImage from '@/assets/logo.png';

export function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login({ email, password });
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        'Email ou senha inválidos';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg-main p-4 sm:p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-10 text-center">
          <img
            src={logoImage}
            alt="Curral Tech"
            className="mx-auto mb-6 h-40 w-auto object-contain sm:h-48"
          />
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
            Painel Administrativo
          </h1>
          <p className="text-sm text-text-secondary">
            Entre com suas credenciais para acessar
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5" aria-label="Formulário de login">
          {error && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg bg-error/10 p-3 text-sm text-error"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-medium text-text-title">
              Email
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Mail size={20} className="text-text-hint" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="block w-full rounded-lg border border-border-strong bg-card py-3 pl-11 pr-4 text-text-title placeholder-gray-400 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="seu@email.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="mb-2 block text-sm font-medium text-text-title">
              Senha
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Lock size={20} className="text-text-hint" />
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="block w-full rounded-lg border border-border-strong bg-card py-3 pl-11 pr-11 text-text-title placeholder-gray-400 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-hint hover:text-text-secondary"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="remember"
              type="checkbox"
              className="size-4 cursor-pointer rounded border-border-strong accent-primary"
            />
            <label htmlFor="remember" className="ml-2 cursor-pointer text-sm text-text-secondary">
              Lembrar de mim
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary text-base font-semibold text-white shadow-sm transition-all hover:bg-primary/90 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <div className="size-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                <span>Entrando…</span>
              </>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-text-secondary">
            © 2024 Curral Tech. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
}
